<template>
  <div class="question_text_area">
    <div v-if="!interactionMeta.isRetakeExplanation || editFreeResponseState">
      <span
        v-show="!interactionMeta.useRichText"
        class="toggleRichText"
        tabindex="0"
        aria-label="toggle use rich text"
        @click.prevent="switchToRichText()"
        @keyup.enter.prevent="switchToRichText()">
          Use rich text?
      </span>
      <v-textarea
        aria-label="type answer here"
        @input="updateAnswer"
        class="free-response"
        :counter="true"
        v-show="!interactionMeta.useRichText"
        :disabled="interactionMeta.isQuestionAnswered || interactionMeta.isProcessingData || (isLiveSession && interactionMeta.pastSessionTime)"
        ref="textarea"
        :value="interactionAttempt.responseText[0]"
        :dark="interactionMeta.darkMode"
        rows="1"
        placeholder="Enter response here"
        auto-grow
      ></v-textarea>
      <div v-show="
        visible
        && interactionMeta.useRichText
        && (
          !isLiveSession
          || (
            isLiveSession
            && !interactionMeta.pastSessionTime
            )
          )">
        <interaction-tinymce
          @tinymce-input="handleTinymceInput"
          :key="tinymceKey"
          ariaLabel="Input field for free response text."
          :inline="false"
          :interactionId="interaction.id"
          :existing-text="interactionAttempt.responseText[0] || ''"
          ref="tinymce"
          :tinymce-key="interaction.id"
          font-size-class="pp-interaction-title"
          :enableUpload="enableUpload"
          :disabledButtons="disabledButtons"
          :attachments.sync="interactionAttempt.interactionAttemptAttachments"
        ></interaction-tinymce>
      </div>
    </div>
    <div class="free-response-text" v-show="interactionMeta.isRetakeExplanation && !editFreeResponseState" v-html="interactionAttempt.responseText[0]" :class="{ whiteText: interaction.position.dark }"></div>
    <div v-if="interactionAttempt.comment" class="feedback-box ma-2 pa-2">
      <p class="font-weight-bold pb-1">Feedback from instructor:</p>
      <div v-html="interactionAttempt.comment"></div>
    </div>
    <interaction-attachments
      v-if="interactionMeta.isInteractionAnswered"
      :attachments="interactionAttempt.interactionAttemptAttachments"
    />
    <div
      v-show="showFeedback"
      class="free-response-feedback clearfix"
      :class="interaction.position.dark ? 'dark-mode-missed' : 'missed'" role="alert">
      <blockquote
        v-show="true"
        :key="interactionTextareaFeedbackKey"
        class="feedback-blockquote clearfix"
        aria-label="interaction feedback'">
        <div
          v-show="true"
          v-html="interaction.config.feedback"
          :class="{ whiteText: interaction.position.dark }" tabindex="0">
        </div>
        <interaction-attachments :attachments="interaction.interactionFeedbackAttachments" />
      </blockquote>
    </div>
  </div>
</template>

<script>
/* global _ */

// Components:
import InteractionAttachments from '@/components/interaction/InteractionAttachments';
import InteractionTinymce from '@/components/interaction/InteractionTinymce';

// Services:
import EventBus from '@/buses/EventBus';

// Plugins / Libraries:
import {
  mapGetters,
  mapState
} from 'vuex';

export default {
  name: 'interaction-textarea',
  props: [
    'interactionId',
    'visible',
  ],
  components: {
    InteractionTinymce,
    InteractionAttachments,
  },
  data() {
    return {
      enableUpload: true,
      interactionTextareaFeedbackKey: 1,
      tinymceKey: 1,
    }
  },
  computed: {
    ...mapState('player', [
      'isLiveSession',
    ]),
    ...mapState('interactions', [
      'editFreeResponseState'
    ]),
    ...mapGetters('player', [
      'bulb',
      'isStrayer',
    ]),
    disabledButtons() {
      if (_.get(this.interaction, 'config.enable_upload', 0)) return '';
      return ['video'];
    },
    interaction() {
      return this.$store.getters['interactions/interaction'](this.interactionId) || {};
    },
    interactionAttempt() {
      return this.$store.getters['interactions/interactionAttempt'](this.interactionId) || {};
    },
    interactionMeta() {
      return this.$store.getters['interactions/interactionMeta'](this.interactionId) || {};
    },
    aria() {
      return {
        label: `Type answer here.`,
      }
    },
    showFeedback() {
      return this.interactionMeta.isInteractionAnswered && _.has(this.interaction, 'config.feedback');
    },
    hasRenderRTEByDefault() {
      return _.get(this, 'bulb.teachers[0].school.config.permissions', []).includes('hasRenderRTEByDefault');
    },
  },
  methods: {
    switchToRichText() {
      if (this.isLiveSession && this.interactionMeta.pastSessionTime) return;
      if (!this.interactionMeta.isProcessingData && !this.interactionMeta.isInteractionAnswered) this.interactionMeta.useRichText = true;
      this.$nextTick(() => EventBus.$emit('applyExistingText', this.interaction.id));
    },
    updateAnswer: _.throttle(function(value) {
        this.$set(this.interactionAttempt.responseText, 0, value);
        this.interactionMeta.isAnswerEmpty = (value.length === 0);
      }, 250, {
        leading: true,
        trailing: true,
      }),
    handleTinymceInput(value) {
      this.$set(this.interactionAttempt.responseText, 0, value);
      this.interactionMeta.isAnswerEmpty = (value.length === 0);
    },
    defaultToRichTextEditorView() {
      if (this.hasRenderRTEByDefault) this.switchToRichText();
    },
  },
  watch: {
    'visible'(newValue, oldValue) {
      if (!newValue && oldValue) this.interactionTextareaFeedbackKey += 1;
      if (newValue) this.tinymceKey += 1;
    },
  },
  mounted() {
    EventBus.$on('retakeInteraction', (interactionId) => {
      if (this.interaction.id === interactionId) {
        this.interactionTextareaFeedbackKey += 1;
        this.defaultToRichTextEditorView();
      }
    });
    this.enableUpload = !!_.get(this.interaction, 'config.enable_upload', true);
    this.defaultToRichTextEditorView();
  }
};
</script>

<style>
.feedback-box {
  background-color: #E5E5E5;
}

.toggleRichText {
  color: var(--branding-active-hex);
}
.toggleRichText:hover{
  cursor: pointer;
}
.whiteText {
  color: white;
}
</style>
